import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import BaseQuestion from '../base-question'
import s from '../styles.module.css'

interface SingleSelectQuestionProps {
  question: {
    id: number
    text: string
    isRequired?: boolean
    type: string
    responses?: Array<{
      id: number
      text: string
      subText?: string
    }>
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: number | string
  error?: string
  onChange?: (value: number | string) => void
}

const SingleSelectQuestion: React.FC<SingleSelectQuestionProps> = ({
  question,
  control,
  errors,
  value,
  error,
  onChange,
}) => {
  const isRHFMode = !!control && !!errors

  if (isRHFMode) {
    // RHF mode - use Controller
    return (
      <BaseQuestion question={question} control={control} errors={errors}>
        <div className={s.responses}>
          <Controller
            name={`question_${question.id}`}
            control={control}
            rules={{
              required: question.isRequired ? 'This field is required' : false,
            }}
            render={({ field }) => (
              <>
                {question.responses?.map(response => {
                  const responseId =
                    typeof response.id === 'string'
                      ? parseInt(response.id, 10)
                      : response.id
                  const fieldValue =
                    typeof field.value === 'string'
                      ? parseInt(field.value, 10)
                      : field.value
                  const isSelected = fieldValue === responseId

                  return (
                    <label
                      key={response.id}
                      className={`${s.responseOption} ${
                        isSelected ? s.selected : ''
                      }`}>
                      <input
                        type="radio"
                        value={response.id}
                        checked={isSelected}
                        onChange={() => field.onChange(response.id)}
                        className={s.responseInput}
                      />
                      <div className={s.responseTextWrapper}>
                        <span className={s.responseText}>{response.text}</span>
                        {response.subText && (
                          <span className={s.responseSubText}>
                            {response.subText}
                          </span>
                        )}
                      </div>
                    </label>
                  )
                })}
              </>
            )}
          />
        </div>
      </BaseQuestion>
    )
  } else {
    // Zustand mode - use direct props
    return (
      <BaseQuestion question={question} error={error}>
        <div className={s.responses}>
          {question.responses?.map(response => {
            const responseId =
              typeof response.id === 'string'
                ? parseInt(response.id, 10)
                : response.id
            const currentValue =
              typeof value === 'string' ? parseInt(value, 10) : value
            const isSelected = currentValue === responseId

            return (
              <label
                key={response.id}
                className={`${s.responseOption} ${
                  isSelected ? s.selected : ''
                }`}>
                <input
                  type="radio"
                  value={response.id}
                  checked={isSelected}
                  onChange={() => onChange?.(response.id)}
                  className={s.responseInput}
                />
                <div className={s.responseTextWrapper}>
                  <span className={s.responseText}>{response.text}</span>
                  {response.subText && (
                    <span className={s.responseSubText}>
                      {response.subText}
                    </span>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      </BaseQuestion>
    )
  }
}

export default SingleSelectQuestion
